// assets/byte-ugc-image-carousel.js

if (!customElements.get('byte-ugc-image-carousel')) {
  class ByteUgcImageCarousel extends HTMLElement {
    constructor() {
      super();

      this.swiperInstances = new Map();
      this.activeTabKey = null;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();

      const initialTabKey = this.tabButtons[0]?.dataset.tabKey || this.panels[0]?.dataset.tabKey;

      if (initialTabKey) {
        this.#activateTab(initialTabKey, { scrollTab: false });
      }
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#destroyAllSwipers();
    }

    handleClick(event) {
      const tabButton = event.target.closest('[data-ugc-tab-button]');
      if (tabButton) {
        this.#activateTab(tabButton.dataset.tabKey, { scrollTab: true });
        return;
      }

      const prevButton = event.target.closest('[data-ugc-prev]');
      if (prevButton) {
        this.#slidePanel(prevButton, 'prev');
        return;
      }

      const nextButton = event.target.closest('[data-ugc-next]');
      if (nextButton) {
        this.#slidePanel(nextButton, 'next');
      }
    }

    handleBlockSelect(event) {
      const selectedSlide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!selectedSlide) return;

      const parentPanel = selectedSlide.closest('[data-ugc-panel]');
      if (!parentPanel) return;

      this.#activateTab(parentPanel.dataset.tabKey, { scrollTab: true });
    }

    #cacheElements() {
      this.tabButtons = [...this.querySelectorAll('[data-ugc-tab-button]')];
      this.panels = [...this.querySelectorAll('[data-ugc-panel]')];
      this.tabsContainer = this.querySelector('[data-ugc-tabs]');
      this.infoTitle = this.querySelector('[data-ugc-info-title]');
      this.infoDescription = this.querySelector('[data-ugc-info-description]');
      this.infoButton = this.querySelector('[data-ugc-info-button]');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.boundHandleBlockSelect);
      }
    }

    #slidePanel(button, direction) {
      const panel = button.closest('[data-ugc-panel]');
      if (!panel) return;

      const swiperInstance = this.swiperInstances.get(panel);
      if (!swiperInstance) return;

      if (direction === 'prev') {
        swiperInstance.slidePrev();
      } else {
        swiperInstance.slideNext();
      }
    }

    #activateTab(tabKey, { scrollTab = false } = {}) {
      if (!tabKey || tabKey === this.activeTabKey) return;

      this.activeTabKey = tabKey;

      for (const tabButton of this.tabButtons) {
        const isActive = tabButton.dataset.tabKey === tabKey;
        tabButton.classList.toggle('is-active', isActive);
        tabButton.setAttribute('aria-pressed', String(isActive));

        if (isActive && scrollTab && this.tabsContainer) {
          const containerRect = this.tabsContainer.getBoundingClientRect();
          const buttonRect = tabButton.getBoundingClientRect();
          const scrollOffset =
            buttonRect.left - containerRect.left - containerRect.width / 2 + buttonRect.width / 2;

          this.tabsContainer.scrollBy({ left: scrollOffset, behavior: 'smooth' });
        }
      }

      let activePanel = null;

      for (const panel of this.panels) {
        const isActive = panel.dataset.tabKey === tabKey;
        panel.hidden = !isActive;

        if (isActive) {
          activePanel = panel;
        }
      }

      if (activePanel) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#ensureSwiperForPanel(activePanel);
          });
        });
      }
    }

    #ensureSwiperForPanel(panel) {
      if (this.swiperInstances.has(panel)) {
        this.swiperInstances.get(panel).update();
        this.#updatePanelState(panel);
        return;
      }

      this.#waitForSwiperAndInit(panel);
    }

    #waitForSwiperAndInit(panel, attempt = 0) {
      if (typeof window.Swiper === 'undefined') {
        if (attempt > 50) return;
        requestAnimationFrame(() => this.#waitForSwiperAndInit(panel, attempt + 1));
        return;
      }

      this.#initSwiper(panel);
    }

    #initSwiper(panel) {
      const swiperContainer = panel.querySelector('[data-ugc-swiper]');
      if (!swiperContainer) return;

      const swiperInstance = new window.Swiper(swiperContainer, {
        slidesPerView: 2.3,
        spaceBetween: 5,
        centeredSlides: true,
        loop: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        autoplay: {
          delay: 3500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        },
        a11y: {
          enabled: true,
          prevSlideMessage: this.dataset.prevLabel || 'Previous slide',
          nextSlideMessage: this.dataset.nextLabel || 'Next slide'
        },
        keyboard: {
          enabled: true
        },
        breakpoints: {
          750: {
            slidesPerView: 2.8,
            spaceBetween: 5,
            centeredSlides: false
          },
          990: {
            slidesPerView: 2.4,
            spaceBetween: 5,
            centeredSlides: true
          },
          1020: {
            slidesPerView: 4.4,
            spaceBetween: 5,
            centeredSlides: true
          },
          1200: {
            slidesPerView: 4.8,
            spaceBetween: 5,
            centeredSlides: true
          }
        },
        on: {
          init: () => this.#updatePanelState(panel),
          slideChangeTransitionStart: () => this.#updatePanelState(panel)
        }
      });

      this.swiperInstances.set(panel, swiperInstance);
    }

    #updatePanelState(panel) {
      // Swiper's active class is the only reliable hook for the current slide
      const activeSlide = panel.querySelector('.swiper-slide-active');
      if (!activeSlide) return;

      const shapeColor = activeSlide.dataset.ugcSlideColor || '';
      const slideTitle = activeSlide.dataset.ugcSlideTitle || '';
      const slideUrl = activeSlide.dataset.ugcSlideUrl || '';
      const hasUrl = slideUrl !== '';

      if (shapeColor) {
        this.style.setProperty('--ugc-shape-color', shapeColor);
      }

      if (this.infoTitle) {
        this.infoTitle.textContent = slideTitle;
        this.infoTitle.hidden = slideTitle === '';
      }

      this.#updateInfoDescription(activeSlide);

      if (this.infoButton) {
        this.infoButton.href = hasUrl ? slideUrl : '#';
        this.infoButton.hidden = !hasUrl;
      }
    }

    #updateInfoDescription(activeSlide) {
      if (!this.infoDescription) return;

      const descriptionTemplate = activeSlide.querySelector('[data-ugc-slide-description]');
      const hasDescription = Boolean(descriptionTemplate?.content.textContent.trim());

      this.infoDescription.replaceChildren();

      if (hasDescription) {
        this.infoDescription.append(descriptionTemplate.content.cloneNode(true));
      }

      this.infoDescription.hidden = !hasDescription;
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);

      if (window.Shopify?.designMode) {
        document.removeEventListener('shopify:block:select', this.boundHandleBlockSelect);
      }
    }

    #destroyAllSwipers() {
      for (const swiperInstance of this.swiperInstances.values()) {
        swiperInstance.destroy(true, true);
      }

      this.swiperInstances.clear();
    }
  }

  customElements.define('byte-ugc-image-carousel', ByteUgcImageCarousel);
}